import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, CheckCircle2, ListTodo, UserX, AlertTriangle, RefreshCw, Briefcase, UserPlus, MessageSquare, PhoneCall } from 'lucide-react';
import { useT } from '../utils/translate';
import CallsOverview from '../components/dashboard/CallsOverview';
import DashSection from '../components/dashboard/DashSection';
import { fmtCompact, currencyLabel } from '../utils/format';
import useCountUp from '../utils/useCountUp';
import { Skeleton, SkeletonCards } from '../components/ui/Skeleton';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';


// ── SVG Gauge (tachometer) ────────────────────────────────────────────────────
function Gauge({ value, max, label, size = 200 }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const sw = 14;
  const pad = sw / 2 + 4;
  const W = size;
  const H = Math.round(size * 0.58);
  const cx = W / 2;
  const r = W / 2 - pad;
  const cy = H;

  // standard math coords: 0°=right, 90°=top, 180°=left
  // SVG: x = cx + r*cos(deg°), y = cy - r*sin(deg°)
  const pt = (deg) => ({
    x: +(cx + r * Math.cos(deg * Math.PI / 180)).toFixed(2),
    y: +(cy - r * Math.sin(deg * Math.PI / 180)).toFixed(2),
  });

  const left  = pt(180); // 0% position
  const right = pt(0);   // 100% position
  const progDeg = 180 - pct * 180;
  const prog  = pt(progDeg);

  // sweep=1 (clockwise on screen, since SVG y grows downward) from left → top → right.
  const bgPath = `M${left.x} ${left.y} A${r} ${r} 0 0 1 ${right.x} ${right.y}`;
  // Sweep never exceeds 180° (this is a semicircle gauge), so the arc from
  // `left` to `prog` is always the minor arc — large-arc-flag is always 0.
  const fgPath = pct > 0.005
    ? `M${left.x} ${left.y} A${r} ${r} 0 0 1 ${prog.x} ${prog.y}`
    : null;

  const arcColor = pct < 0.5 ? '#ef4444' : pct < 0.8 ? '#f59e0b' : '#22c55e';

  // Needle
  const nl = r - sw - 6;
  const np = {
    x: +(cx + nl * Math.cos(progDeg * Math.PI / 180)).toFixed(2),
    y: +(cy - nl * Math.sin(progDeg * Math.PI / 180)).toFixed(2),
  };

  return (
    <svg width={W} height={H + 4} viewBox={`0 0 ${W} ${H + 4}`}>
      <path d={bgPath} fill="none" style={{ stroke: 'rgb(var(--c-s100))' }} strokeWidth={sw} strokeLinecap="round" />
      {fgPath && <path d={fgPath} fill="none" stroke={arcColor} strokeWidth={sw} strokeLinecap="round" />}
      <line x1={cx} y1={cy} x2={np.x} y2={np.y} style={{ stroke: 'rgb(var(--c-ink))' }} strokeWidth={2.5} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={5} style={{ fill: 'rgb(var(--c-ink))' }} />
      <text x={cx} y={cy - r * 0.45} textAnchor="middle" fontSize={size * 0.11} fontWeight="800" style={{ fill: 'rgb(var(--c-ink))' }}>
        {Math.round(pct * 100)}%
      </text>
      <text x={cx} y={cy - r * 0.22} textAnchor="middle" fontSize={size * 0.055} style={{ fill: 'rgb(var(--c-ink-3))' }}>
        {label}
      </text>
    </svg>
  );
}

// ── SVG Pie Chart ─────────────────────────────────────────────────────────────
function PieChart({ data, size = 160 }) {
  // data items already have .label and .color from backend
  const t = useT();
  const [hovered, setHovered] = useState(null);
  const cx = size / 2, cy = size / 2, r = size / 2 - 8;
  const total = data.reduce((a, d) => a + d.count, 0);
  if (total === 0) return null;

  let angle = -Math.PI / 2; // start from top
  const slices = data.map((d, i) => {
    const pct   = d.count / total;
    const start = angle;
    const sweep = pct * 2 * Math.PI;
    angle += sweep;
    const end = angle;

    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = sweep > Math.PI ? 1 : 0;
    const path  = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;

    const midAngle = start + sweep / 2;
    return { ...d, path, midAngle, pct };
  });

  const hov = hovered !== null ? slices[hovered] : null;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      {slices.map((s, i) => {
        const scale = hovered === i ? 1.04 : 1;
        return (
          <path
            key={i}
            d={s.path}
            fill={s.color}
            style={{ stroke: 'rgb(var(--c-card))' }}
            strokeWidth={2}
            opacity={hovered !== null && hovered !== i ? 0.55 : 1}
            style={{ transform: `scale(${scale})`, transformOrigin: `${cx}px ${cy}px`, transition: 'all 0.15s' }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            className="cursor-pointer"
          />
        );
      })}
      {/* Center donut hole */}
      <circle cx={cx} cy={cy} r={r * 0.48} style={{ fill: 'rgb(var(--c-card))' }} />
      {/* Center text */}
      {hov ? (
        <>
          <text x={cx} y={cy - 7} textAnchor="middle" fontSize="15" fontWeight="700" style={{ fill: 'rgb(var(--c-ink))' }}>
            {Math.round(hov.pct * 100)}%
          </text>
          <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" style={{ fill: 'rgb(var(--c-ink-3))' }}>
            {hov.label}
          </text>
        </>
      ) : (
        <>
          <text x={cx} y={cy - 5} textAnchor="middle" fontSize="11" fontWeight="600" style={{ fill: 'rgb(var(--c-ink-3))' }}>
            {t('dashboardHome.total')}
          </text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="18" fontWeight="800" style={{ fill: 'rgb(var(--c-ink))' }}>
            {total}
          </text>
        </>
      )}
    </svg>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, tone, label, value, sub, onClick, keepSub = false }) {
  const shown = useCountUp(value);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`glass rounded-2xl p-4 sm:p-5 flex flex-col gap-2 sm:gap-3 text-left w-full transition-all duration-150 ease-soft hover:-translate-y-0.5 animate-rise ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[13px] font-medium text-ink-secondary">{label}</span>
      </div>
      <p className="text-3xl font-bold text-ink leading-none tracking-tight tnum">{shown ?? '—'}</p>
      {sub && <p className={`text-xs text-ink-tertiary leading-snug ${keepSub ? "" : "hidden sm:block"}`}>{sub}</p>}
    </button>
  );
}

// ── Thin progress bar ─────────────────────────────────────────────────────────
function Bar({ pct }) {
  const color = pct < 0.5 ? 'bg-red-400' : pct < 0.8 ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full origin-left animate-fill-bar ${color}`} style={{ width: `${Math.min(pct, 1) * 100}%` }} />
    </div>
  );
}

const Avatar = ({ name }) => (
  <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex items-center justify-center shrink-0">
    {(name || '?').charAt(0).toUpperCase()}
  </span>
);

const Card = ({ className = '', children }) => (
  <div className={`bg-white rounded-2xl border border-surface-200 ${className}`}>{children}</div>
);

const CardHead = ({ title, right }) => (
  <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-surface-100">
    <h3 className="text-sm font-semibold text-ink">{title}</h3>
    {right}
  </div>
);

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DashboardHome() {
  const navigate = useNavigate();
  const t = useT();
  const fmtSum = (v) => fmtCompact(v, t);
  const cur = currencyLabel('UZS', t);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/dashboard`, { params: { tz: new Date().getTimezoneOffset() } });
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
          <Skeleton className="h-5 w-48" />
          <SkeletonCards count={5} />
          <Skeleton className="h-40 w-full" rounded="rounded-2xl" />
          <Skeleton className="h-56 w-full" rounded="rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-ink-tertiary">
        <AlertTriangle className="w-8 h-8" />
        <p className="text-sm">{t('dashboardHome.loadError')}</p>
        <button onClick={load} className="btn-primary btn-sm flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> {t('dashboardHome.retry')}
        </button>
      </div>
    );
  }

  const { cards, kpi = {}, dealSources = [], dealsByManager = [], managerTotal = { count: 0, sum: 0 }, goalsProgress = null, funnelStats = [] } = data;
  const hasGoals = goalsProgress && (goalsProgress.totalSumTarget > 0 || goalsProgress.totalCountTarget > 0);
  const goalUsers = (goalsProgress?.byUser || []).filter(u => u.targetSum > 0 || u.targetCount > 0);
  const hasSales = hasGoals || dealsByManager.length > 0 || dealSources.length > 0;

  return (
    <div className="h-full bg-surface-50 overflow-y-auto">
      <div className="p-4 sm:p-6 lg:p-8 space-y-8 max-w-7xl mx-auto">

        {/* 0. Ключевые цифры: главное, что собственник хочет увидеть сразу */}
        <DashSection title={t('dashboardHome.secOverview')}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              icon={Briefcase} tone="bg-primary-50 text-primary-700"
              label={t('dashboardHome.kpiOpenDeals')} value={kpi.openDeals}
              sub={managerTotal.sum > 0 ? `${fmtSum(managerTotal.sum)} ${cur}` : null} keepSub
              onClick={funnelStats[0] ? () => navigate(`/funnel/${funnelStats[0]._id}`) : undefined}
            />
            <StatCard
              icon={UserPlus} tone="bg-emerald-50 text-emerald-700"
              label={t('dashboardHome.kpiNewContacts')} value={kpi.todayContacts}
              sub={t('dashboardHome.kpiNewContactsSub')}
              onClick={() => navigate('/contacts')}
            />
            <StatCard
              icon={MessageSquare} tone={kpi.unansweredConvs > 0 ? 'bg-amber-50 text-amber-700' : 'bg-surface-100 text-ink-secondary'}
              label={t('dashboardHome.kpiUnanswered')} value={kpi.unansweredConvs}
              sub={t('dashboardHome.kpiUnansweredSub')}
              onClick={() => navigate('/inbox')}
            />
            <StatCard
              icon={AlertCircle} tone={cards.overdueCount > 0 ? 'bg-red-50 text-red-700' : 'bg-surface-100 text-ink-secondary'}
              label={t('dashboardHome.overdueLabel')} value={cards.overdueCount}
              sub={cards.overdueCount > 0 ? t('dashboardHome.overdueSubAlert') : t('dashboardHome.overdueSubOk')}
              onClick={() => navigate('/tasks')}
            />
            <StatCard
              icon={PhoneCall} tone="bg-blue-50 text-blue-700"
              label={t('dashboardHome.kpiCalls')} value={kpi.todayCalls}
              sub={t('dashboardHome.kpiCallsSub')}
              onClick={() => navigate('/calls')}
            />
          </div>
        </DashSection>

        {/* 1. Звонки */}
        <CallsOverview />

        {/* 2. Задачи */}
        <DashSection title={t('dashboardHome.secTasks')}>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              icon={CheckCircle2} tone="bg-emerald-50 text-emerald-700"
              label={t('dashboardHome.completedLabel')} value={cards.completedCount}
              sub={t('dashboardHome.completedSub')}
              onClick={() => navigate('/tasks')}
            />
            <StatCard
              icon={ListTodo} tone="bg-blue-50 text-blue-600"
              label={t('dashboardHome.activeLabel')} value={cards.activeCount}
              sub={t('dashboardHome.activeSub')}
              onClick={() => navigate('/tasks')}
            />
            <StatCard
              icon={UserX} tone="bg-amber-50 text-amber-700"
              label={t('dashboardHome.unassignedLabel')} value={cards.unassignedCount}
              sub={cards.unassignedSum > 0 ? `${fmtSum(cards.unassignedSum)} ${cur}` : t('dashboardHome.unassignedSubNoLeads')}
              onClick={funnelStats[0] ? () => navigate(`/funnel/${funnelStats[0]._id}`) : undefined}
            />
          </div>
        </DashSection>

        {/* 3. Продажи */}
        {hasSales && (
          <DashSection title={t('dashboardHome.secSales')}>
            <div className="space-y-4">

              {hasGoals && (
                <Card>
                  <CardHead title={t('dashboardHome.goalsTitle')} />
                  <div className="p-5 grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
                    <div className={`${goalUsers.length ? 'lg:col-span-3' : 'lg:col-span-5'} flex items-end justify-center gap-8 flex-wrap`}>
                      {goalsProgress.totalSumTarget > 0 && (
                        <div className="flex flex-col items-center gap-2">
                          <Gauge value={goalsProgress.currentSum} max={goalsProgress.totalSumTarget} label={t('dashboardHome.gaugeSum')} size={190} />
                          <p className="text-sm font-bold text-ink">{fmtSum(goalsProgress.currentSum)} <span className="text-xs text-ink-tertiary font-normal">/ {fmtSum(goalsProgress.totalSumTarget)} {cur}</span></p>
                        </div>
                      )}
                      {goalsProgress.totalCountTarget > 0 && (
                        <div className="flex flex-col items-center gap-2">
                          <Gauge value={goalsProgress.currentCount} max={goalsProgress.totalCountTarget} label={t('dashboardHome.gaugeDealCount')} size={190} />
                          <p className="text-sm font-bold text-ink">{goalsProgress.currentCount} <span className="text-xs text-ink-tertiary font-normal">/ {goalsProgress.totalCountTarget} {t('dashboardHome.unit')}</span></p>
                        </div>
                      )}
                    </div>

                    {goalUsers.length > 0 && (
                      <div className="lg:col-span-2 lg:border-l lg:border-surface-100 lg:pl-6 space-y-4 max-h-64 overflow-y-auto">
                        <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wide">{t('dashboardHome.byEmployee')}</p>
                        {goalUsers.map((u, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <Avatar name={u.name} />
                            <div className="flex-1 min-w-0 space-y-2">
                              <p className="text-sm font-medium text-ink truncate">{u.name}</p>
                              {u.targetSum > 0 && (
                                <div className="space-y-1">
                                  <div className="flex justify-between text-xs text-ink-tertiary">
                                    <span>{t('dashboardHome.donutSum')}</span>
                                    <span>{fmtSum(u.currentSum)} / {fmtSum(u.targetSum)}</span>
                                  </div>
                                  <Bar pct={u.currentSum / u.targetSum} />
                                </div>
                              )}
                              {u.targetCount > 0 && (
                                <div className="space-y-1">
                                  <div className="flex justify-between text-xs text-ink-tertiary">
                                    <span>{t('dashboardHome.donutCount')}</span>
                                    <span>{u.currentCount} / {u.targetCount} {t('dashboardHome.unit')}</span>
                                  </div>
                                  <Bar pct={u.currentCount / u.targetCount} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {(dealsByManager.length > 0 || dealSources.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

                  {dealsByManager.length > 0 && (
                    <Card className="lg:col-span-3 overflow-hidden flex flex-col">
                      <CardHead
                        title={t('dashboardHome.byManager')}
                        right={
                          <div className="flex items-center gap-3 text-right">
                            <div>
                              <p className="text-xs text-ink-tertiary leading-none mb-0.5">{t('dashboardHome.dealsCountLabel')}</p>
                              <p className="text-sm font-bold text-ink">{managerTotal.count} {t('dashboardHome.unit')}</p>
                            </div>
                            <div className="w-px h-7 bg-surface-200" />
                            <div>
                              <p className="text-xs text-ink-tertiary leading-none mb-0.5">{t('dashboardHome.sumLabel')}</p>
                              <p className="text-sm font-bold text-primary-600">{fmtSum(managerTotal.sum)} {cur}</p>
                            </div>
                          </div>
                        }
                      />
                      <div className="divide-y divide-surface-100 flex-1">
                        {dealsByManager.map((m, i) => {
                          const pct = managerTotal.count > 0 ? m.count / managerTotal.count : 0;
                          return (
                            <div key={i} className="flex items-center gap-3 px-5 py-3">
                              <Avatar name={m.name} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-sm font-medium text-ink truncate">{m.name}</span>
                                  <span className="text-xs text-ink-tertiary shrink-0 ml-2">{m.count} {t('dashboardHome.unit')}</span>
                                </div>
                                <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-primary-500 rounded-full" style={{ width: `${pct * 100}%` }} />
                                </div>
                              </div>
                              <div className="text-right shrink-0 w-20">
                                <p className="text-sm font-semibold text-ink">{fmtSum(m.sum)}</p>
                                <p className="text-xs text-ink-tertiary">{cur}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}

                  {dealSources.length > 0 && (
                    <Card className={`${dealsByManager.length > 0 ? 'lg:col-span-2' : 'lg:col-span-5'} flex flex-col`}>
                      <CardHead title={t('dashboardHome.dealSources')} />
                      <div className="flex-1 p-5 flex items-center justify-center gap-6 flex-wrap">
                        <PieChart data={dealSources} size={160} />
                        <div className="flex flex-col gap-2.5">
                          {dealSources.map((d, i) => (
                            <div key={i} className="flex items-center gap-2.5">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color || '#94a3b8' }} />
                              <span className="text-sm text-ink-secondary min-w-[70px]">{d.label || d.source || t('dashboardHome.other')}</span>
                              <span className="text-sm font-semibold text-ink">{d.pct}%</span>
                              <span className="text-xs text-ink-tertiary">({d.count} {t('dashboardHome.unit')})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </DashSection>
        )}

      </div>
    </div>
  );
}
